/* ==========================================================================
   Location Section — assets/location-section.js
   Vanilla JS, no dependencies. Pairs exactly with sections/location.liquid
   and assets/location-section.css. Supports multiple section instances per
   page and re-initializes correctly inside the Shopify Theme Editor.
   ========================================================================== */

(function () {
  'use strict';

  /**
   * Wires up a single Location Section instance.
   * @param {HTMLElement} root - the <section class="location-section"> element
   */
  function initLocationSection(root) {
    if (!root || root.dataset.lsInitialized === 'true') return;

    var selector = root.querySelector('[data-location-selector]');
    var trigger = root.querySelector('[data-location-trigger]');
    var triggerLabel = root.querySelector('[data-location-trigger-label]');
    var listbox = root.querySelector('[data-location-listbox]');
    var options = Array.prototype.slice.call(root.querySelectorAll('[data-location-option]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-location-panel]'));
    var mapWrap = root.querySelector('[data-location-map]');
    var mapIframe = root.querySelector('[data-location-map-iframe]');
    var mapDataScript = root.querySelector('[data-location-map-data]');

    // Nothing to wire up if there are no locations, or required nodes are missing.
    if (!selector || !trigger || !listbox || options.length === 0) return;

    root.dataset.lsInitialized = 'true';

    var mapData = {};
    if (mapDataScript) {
      try {
        mapData = JSON.parse(mapDataScript.textContent);
      } catch (err) {
        mapData = {};
      }
    }

    var isOpen = false;
    var closeTimer = null;

    /* ---------------------------------------------------------------- */
    /* Dropdown open / close                                            */
    /* ---------------------------------------------------------------- */

    function positionList() {
      var triggerRect = trigger.getBoundingClientRect();
      var estimatedHeight = Math.min(listbox.scrollHeight || 280, 280);
      var spaceBelow = window.innerHeight - triggerRect.bottom;
      var spaceAbove = triggerRect.top;

      if (spaceBelow < estimatedHeight + 12 && spaceAbove > spaceBelow) {
        selector.classList.add('is-flipped');
      } else {
        selector.classList.remove('is-flipped');
      }
    }

    function openList(focusActiveOption) {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      isOpen = true;
      listbox.hidden = false;
      // Force layout so the browser registers hidden -> visible before we
      // add the class that animates opacity/transform.
      void listbox.offsetHeight;
      selector.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      positionList();

      if (focusActiveOption) {
        var activeIndex = getActiveIndex();
        focusOptionAt(activeIndex > -1 ? activeIndex : 0);
      }
    }

    function closeList(returnFocusToTrigger) {
      isOpen = false;
      selector.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');

      // Delay hiding so the CSS opacity/transform close transition can play.
      if (closeTimer) window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(function () {
        listbox.hidden = true;
      }, 200);

      if (returnFocusToTrigger) trigger.focus();
    }

    function toggleList() {
      if (isOpen) {
        closeList(true);
      } else {
        openList(true);
      }
    }

    /* ---------------------------------------------------------------- */
    /* Option focus / selection                                         */
    /* ---------------------------------------------------------------- */

    function getActiveIndex() {
      for (var i = 0; i < options.length; i++) {
        if (options[i].classList.contains('is-active')) return i;
      }
      return -1;
    }

    function focusOptionAt(index) {
      if (index < 0 || index >= options.length) return;
      options[index].focus();
      trigger.setAttribute('aria-activedescendant', options[index].id);
    }

    /**
     * Activates a location by its block id: updates the trigger label,
     * option/panel active + aria states, and swaps the map iframe.
     * Does NOT touch dropdown open/close state, so it can be reused for
     * Theme Editor previews as well as normal selection.
     */
    function activateByBlockId(blockId) {
      var matchedOption = null;

      options.forEach(function (option) {
        var isMatch = option.dataset.locationTarget === blockId;
        if (isMatch) matchedOption = option;
        option.classList.toggle('is-active', isMatch);
        option.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });

      if (!matchedOption) return;

      if (triggerLabel) triggerLabel.textContent = matchedOption.textContent.trim();

      panels.forEach(function (panel) {
        var isMatch = panel.dataset.locationId === blockId;
        panel.classList.toggle('is-active', isMatch);
        if (isMatch) {
          panel.removeAttribute('aria-hidden');
        } else {
          panel.setAttribute('aria-hidden', 'true');
        }
      });

      updateMap(blockId);
    }

    function selectOption(optionEl) {
      if (!optionEl) return;
      activateByBlockId(optionEl.dataset.locationTarget);
      closeList(true);
    }

    function updateMap(blockId) {
      if (!mapIframe || !mapWrap) return;
      var url = mapData[blockId];
      if (!url) return;
      if (mapIframe.getAttribute('src') === url) return; // already showing this location

      mapWrap.classList.add('is-loading');
      var settled = false;

      function onLoad() {
        if (settled) return;
        settled = true;
        mapWrap.classList.remove('is-loading');
        mapIframe.removeEventListener('load', onLoad);
      }

      mapIframe.addEventListener('load', onLoad);
      mapIframe.setAttribute('src', url);

      // Fallback in case the load event is delayed/blocked.
      window.setTimeout(function () {
        if (!settled) onLoad();
      }, 1500);
    }

    /* ---------------------------------------------------------------- */
    /* Event bindings                                                    */
    /* ---------------------------------------------------------------- */

    trigger.addEventListener('click', toggleList);

    trigger.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        if (!isOpen) openList(true);
      } else if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        closeList(true);
      }
    });

    options.forEach(function (option) {
      option.addEventListener('click', function () {
        selectOption(option);
      });
    });

    listbox.addEventListener('keydown', function (event) {
      var currentIndex = options.indexOf(document.activeElement);
      if (currentIndex === -1) currentIndex = getActiveIndex();

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          focusOptionAt((currentIndex + 1) % options.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          focusOptionAt((currentIndex - 1 + options.length) % options.length);
          break;
        case 'Home':
          event.preventDefault();
          focusOptionAt(0);
          break;
        case 'End':
          event.preventDefault();
          focusOptionAt(options.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          selectOption(options[currentIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          closeList(true);
          break;
        case 'Tab':
          closeList(false);
          break;
      }
    });

    document.addEventListener('click', function (event) {
      if (isOpen && !selector.contains(event.target)) {
        closeList(false);
      }
    });

    window.addEventListener('resize', function () {
      if (isOpen) positionList();
    });

    // Expose an activation hook so the global Theme Editor listener below
    // can preview a location when its block is selected in the sidebar.
    root.__locationSectionActivate = activateByBlockId;
  }

  function initAll(scope) {
    var context = scope || document;
    var sections = context.querySelectorAll('[data-section-type="location-section"]');
    sections.forEach(function (section) {
      initLocationSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  /* ---------------------------------------------------------------- */
  /* Shopify Theme Editor integration                                  */
  /* ---------------------------------------------------------------- */

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target.querySelector('[data-section-type="location-section"]');
      if (section) initLocationSection(section);
    });

    document.addEventListener('shopify:block:select', function (event) {
      var section = event.target.closest('[data-section-type="location-section"]');
      if (!section || typeof section.__locationSectionActivate !== 'function') return;

      var blockId =
        (event.detail && event.detail.blockId) ||
        (event.target.dataset ? event.target.dataset.locationId : null);

      if (blockId) section.__locationSectionActivate(blockId);
    });
  }
})();